"use client";

import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { HashIcon, PercentIcon } from "lucide-react";
import type { DiffDisplayMode } from "@/app/[lang]/[workspaceId]/_components/table/diff-cell";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../../../../components/ui/tooltip";

interface DiffDisplayToggleProps {
  value: DiffDisplayMode;
  onChange: (mode: DiffDisplayMode) => void;
  className?: string;
}

/**
 * 差分表示モードの切り替えトグル
 * 実数表示と割合表示を切り替えるUIコンポーネント
 */
export function DiffDisplayToggle({
  value,
  onChange,
  className,
}: DiffDisplayToggleProps) {
  const { t } = useLingui();
  const isAbsolute = value === "absolute";

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className={cn(
              "ml-1 p-0.5 rounded hover:bg-muted flex items-center",
              className,
            )}
            onClick={(e) => {
              e.stopPropagation();
              onChange(isAbsolute ? "percentage" : "absolute");
            }}
            aria-label={t(msg`Toggle between absolute and percentage display`)}
          >
            {isAbsolute ? (
              <HashIcon className="size-3" aria-hidden="true" />
            ) : (
              <PercentIcon className="size-3" aria-hidden="true" />
            )}
          </button>
        </TooltipTrigger>
        <TooltipContent>
          {isAbsolute ? (
            <>
              <p>
                <Trans>Currently showing actual numbers</Trans>
              </p>
              <p className="text-xs opacity-70">
                <Trans>Click to switch to percentage</Trans>
              </p>
            </>
          ) : (
            <>
              <p>
                <Trans>Currently showing percentage change</Trans>
              </p>
              <p className="text-xs opacity-70">
                <Trans>Click to switch to actual numbers</Trans>
              </p>
            </>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
